import type { UserWithDepartments } from '../../../responses/users'
import { combineReducers } from 'redux'
import { createPaginatedAction } from '../../../utils/redux-utils'
import { createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import { combineEpics } from 'redux-observable'
import type { RootEpic } from '../../root'
import { concatMap, filter, map, mergeMap, startWith, switchMap, takeUntil } from 'rxjs/operators'
import { apiRequests } from '../../../http/config'
import { paginate } from '../../../utils/rx-operators'
import { toastFailedResponse } from '../../../components/controls/Toaster/Toaster'
import type { Entity } from '../../../types/UtilityTypes'
import { entityReplace } from '../../../utils/entity-utils'

const LIMIT = 30

const actions = {
    list: createPaginatedAction(
        '@SETTINGS_USERS/LIST_INIT',
        '@SETTINGS_USERS/LIST_MORE',
        '@SETTINGS_USERS/LIST_SUCCESS',
        '@SETTINGS_USERS/LIST_FAILURE',
    )<string, UserWithDepartments[]>(),
    update: createAsyncAction(
        '@SETTINGS_USERS/UPDATE_REQUEST',
        '@SETTINGS_USERS/UPDATE_SUCCESS',
        '@SETTINGS_USERS/UPDATE_FAILURE',
    )<Partial<UserWithDepartments> & Entity, UserWithDepartments, void>(),
}

export {actions as usersActions}

export interface UsersState {
    search: string,
    data: UserWithDepartments[],
}

export const usersReducer = combineReducers<UsersState>({
    search: createReducer('')
        .handleAction(actions.list.init, (_, action) => action.payload),
    data: createReducer<UserWithDepartments[]>([])
        .handleAction(actions.list.init, () => [])
        .handleAction(actions.list.success, (state, action) => state.concat(action.payload))
        .handleAction(actions.update.success, (state, action) => entityReplace(action.payload)(state))
})

const list: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.list.init)),
    switchMap(action => action$.pipe(
        startWith(actions.list.more()),
        takeUntil(filter(isActionOf(actions.list.init))(action$)),
        filter(isActionOf(actions.list.more)),
        paginate(LIMIT),
        concatMap(offset => apiRequests.users.list({
            query: {
                offset,
                limit: LIMIT,
                search: action.payload,
                all: 1,
            }
        })),
        map(res => res.response.success?
            actions.list.success(res.response.rows):
            actions.list.failure(toastFailedResponse(res.response, 'Не удалось получить список пользователей'))
        ),
    )),
)

const update: RootEpic = action$ => action$.pipe(
    filter(isActionOf(actions.update.request)),
    mergeMap(action => apiRequests.users.update({
        data: action.payload,
        params: {id: action.payload.id},
    })),
    map(res => res.response.success?
        actions.update.success(res.response.row):
        actions.update.failure(toastFailedResponse(res.response, 'Не удалось обновить пользователя'))
    )
)

export const usersEpic = combineEpics(list, update)